import React from "react";
import { Eye, ChevronLeft, ChevronRight } from "react-feather";
import { useNavigate } from "react-router-dom";
import Table, { TableBody, TableHeading } from "../../../shared/Table/Table";
import { Card } from "../../../shared/Card/Card";
import { PrimaryButton } from "../../../shared/Buttons/Buttons";
import "./interviewLogTable.scss";

interface InterviewLogTableProps {
  handlePageChange: (page: number) => void;
  currentPage: number;
  interviewLogs: any[];
  total: number;
  totalPages: number;
  loading: boolean;
}

const InterviewLogTable: React.FC<InterviewLogTableProps> = ({
  handlePageChange,
  currentPage,
  interviewLogs,
  total,
  totalPages,
  loading,
}) => {
  const navigate = useNavigate();

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return <span className="badge badge-completed">Completed</span>;
      case "in_progress":
        return <span className="badge badge-in-progress">In Progress</span>;
      case "pending":
        return <span className="badge badge-pending">Pending</span>;
      default:
        return <span className="badge badge-default">{status}</span>;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return "score-green";
    if (score >= 80) return "score-blue";
    if (score >= 70) return "score-yellow";
    return "score-red";
  };

  if (loading) {
    return (
      <Card>
        <div className="table-loading">
          <div className="loading-content">
            <div className="spinner" />
            <span>Loading interview logs...</span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="interview-log-table-container">
        <Table full>
          <TableHeading>
            <th>User</th>
            <th>Interview Type</th>
            <th>Score</th>
            <th>Duration</th>
            <th>Date</th>
            <th>Status</th>
            <th className="text-right">Actions</th>
          </TableHeading>
          <TableBody>
            {interviewLogs.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty-cell">
                  <div className="empty-content">No interview logs found</div>
                </td>
              </tr>
            ) : (
              interviewLogs.map((interview: any) => (
                <tr key={interview.id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {interview.user.name
                          .split(" ")
                          .map((n: string) => n[0])
                          .join("")
                          .toUpperCase()}
                      </div>
                      <div>
                        <div className="user-name">{interview.user.name}</div>
                        <div className="user-email">{interview.user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="interview-type-badge">
                      {interview.interviewType}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`score ${getScoreColor(
                        (interview.score || 0) * 10
                      )}`}
                    >
                      {Math.round((interview.score || 0) * 10)}%
                    </span>
                  </td>
                  <td>{interview.duration || "0m 0s"}</td>
                  <td>
                    {new Date(interview.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </td>
                  <td>{getStatusBadge(interview.status)}</td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="action-button"
                      onClick={() =>
                        navigate(`/interview-logs/${interview.id}`)
                      }
                    >
                      <Eye className="action-icon" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <div className="table-pagination">
          <div className="pagination-info">
            {total > 0 ? (
              <>
                Showing {(currentPage - 1) * 10 + 1} to{" "}
                {Math.min(currentPage * 10, total)} of {total} results
              </>
            ) : (
              "No results found"
            )}
          </div>
          <div className="pagination-buttons">
            <PrimaryButton
              text="Previous"
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disable={currentPage === 1}
              size="sm"
            />
            <span className="pagination-page-info">
              Page {currentPage} of {totalPages}
            </span>
            <PrimaryButton
              text="Next"
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disable={currentPage >= totalPages}
              size="sm"
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default InterviewLogTable;
